import React, { useMemo } from 'react';
import { ImagePlus } from 'lucide-react';
import { BaseTool, type IToolComponentProps, type IToolContext, type IToolManifest } from '../../core/tools/interfaces/ITool';
import type { ICommand } from '../../core/commands/ICommand';
import { isOrgPersonNodeType } from '../../domains/orgchart/models/defaults';
import { useTranslation } from '../../hooks/useTranslation';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

type ImageReplaceTargetType = 'image-node' | 'org-person';

type OpenImageUploadModalOptions = {
  mode?: 'insert' | 'replace';
  maxFiles?: number;
  title?: string;
  description?: string;
  replaceTarget?: {
    nodeId: string;
    targetType: ImageReplaceTargetType;
    hasImage: boolean;
  };
};

type SelectedReplaceTarget = {
  nodeId: string;
  targetType: ImageReplaceTargetType;
  hasImage: boolean;
} | null;

const getShapeData = (context: IToolContext, nodeId: string): Record<string, unknown> | null => {
  const shape = context.shapes.get(nodeId) as any;
  const data = shape?.data ?? shape;
  if (!data || typeof data !== 'object') return null;
  return data as Record<string, unknown>;
};

export const getSelectedReplaceTarget = (context: IToolContext): SelectedReplaceTarget => {
  if (context.selection.size !== 1) return null;
  const [nodeId] = Array.from(context.selection);
  const data = getShapeData(context, nodeId);
  if (!data) return null;

  const type = typeof data.type === 'string' ? data.type : '';
  const attrs = ((data.attrs as Record<string, unknown> | undefined) ?? {});
  const topLevelObjectKey = typeof data.objectKey === 'string' ? data.objectKey : '';
  const attrObjectKey = typeof attrs.objectKey === 'string' ? attrs.objectKey : '';
  const topLevelPhotoRef = typeof data.photoRef === 'string' ? data.photoRef : '';
  const attrPhotoRef = typeof attrs.photoRef === 'string' ? attrs.photoRef : '';

  if (type === 'image') {
    const hasImage = Boolean((attrObjectKey || topLevelObjectKey).trim());
    return { nodeId, targetType: 'image-node', hasImage };
  }

  if (isOrgPersonNodeType(type)) {
    const hasImage = Boolean((attrPhotoRef || topLevelPhotoRef).trim());
    return { nodeId, targetType: 'org-person', hasImage };
  }

  return null;
};

const ImageReplaceToolButton: React.FC<IToolComponentProps> = ({ context }) => {
  const { t } = useTranslation('diagram');
  const replaceTarget = useMemo(() => getSelectedReplaceTarget(context), [context]);
  if (!replaceTarget) return null;

  const label = replaceTarget.hasImage
    ? (t('tools.replaceImage', { defaultValue: 'Replace image' }) as string)
    : (t('tools.addImage', { defaultValue: 'Add image' }) as string);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
            data-testid="tid.editor.tool.image-replace"
            aria-label={label}
            onClick={() => {
              try {
                (window as any).__openImageUploadModal?.({
                  mode: 'replace',
                  maxFiles: 1,
                  title: label,
                  description: replaceTarget.hasImage
                    ? (t('imageUploadModal.replaceFlowDescription', {
                        defaultValue: 'Upload a new image to replace the current one.',
                      }) as string)
                    : (t('imageUploadModal.addImageForShapeDescription', {
                        defaultValue: 'Upload an image for this shape.',
                      }) as string),
                  replaceTarget,
                } as OpenImageUploadModalOptions);
              } catch {
                // no-op
              }
            }}
          >
            <ImagePlus className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export class ImageReplaceTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'image-replace',
    name: 'tools.replaceImage',
    icon: 'image',
    category: 'edit',
    description: 'Add or replace image for selected node',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false,
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    return getSelectedReplaceTarget(context) !== null;
  }

  execute(): ICommand | null {
    return null;
  }

  getFloatingPanelComponent() {
    return ImageReplaceToolButton;
  }
}
